// A module's player change (05c) — main.js's `.player-select` 'change' listener and its
// linked players (Ben's #178), moved here.
//
// Every player change sets the hidden `.player-select` first (undo reads the player off
// it), then runs onPlayerSelectChange: the search box's pick and a linked pick by firing
// 'change' (05c's fork 1A), the drag-switch, a comparison dropping back to one player and
// the "Try Katie McCabe" link by calling it directly (05g-1 — drop-actions.ts,
// TryTestPlayerLink.tsx). (main.js's mobile picker fired 'change' too, until 05k-1.) Same
// handler either way, so each keeps its exact side effects; it also writes the store's
// `player`.

import { flushSync } from "react-dom";
import type { useProtoStore } from "../store/store.js";
import { linkDecision, linkHintText } from "./player-picker.js";
import { setStickyPlayer } from "./sticky-player.js";
import { playerIdFromSelect } from "./module-shell.js";
import type { PlayerId } from "../data/players.js";
import { isSelected, selectedModules } from "./selection.js";
import { ensurePlayerOption, hasPlayerOption } from "./player-options.js";
import { LIVE } from "../live/flag.js";
import { pickedPlayer } from "../live/picked-players.js";
import { isSnapshotPlayer } from "../data/players.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { flashLinkSkipped, followPlayerLeague, followsLeague } from "./league-follow.js";

type ProtoStore = typeof useProtoStore;

/** The module-element flags this handler reads / sets (the `_`-state convention). */
interface PlayerFlags {
  _skipNextSaveState?: boolean;
  _applyingLinkedPlayer?: boolean;
  _skipBodyOpacity?: boolean;
  _mergedPlayers?: unknown;
}
const flagsOf = (mod: HTMLElement) => mod as unknown as PlayerFlags;

// ── Linked players ───────────────────────────────────────────────────────────
// Changing the player in one selected module changes it in every other selected module
// that can take it; the ones that can't flash briefly so it's clear they were skipped.

function linkedPlayerTargets(sourceMod: HTMLElement): HTMLElement[] {
  if (!isSelected(sourceMod)) return [];
  return selectedModules().filter((m) => m !== sourceMod);
}

function applyLinkedPlayer(targets: HTMLElement[], playerId: PlayerId): void {
  // The select holds ids as text (an <option> value always is).
  const value = String(playerId);
  // A live pick the snapshot doesn't have (ids-3): every picker takes her (her position
  // isn't known yet), so she gets an option in each target first.
  const picked = LIVE && !isSnapshotPlayer(snapshotPlayersOrNone(), playerId) ? pickedPlayer(playerId) : undefined;
  targets.forEach((mod) => {
    const select = mod.querySelector<HTMLSelectElement>(".player-select");
    if (select && picked && !flagsOf(mod)._mergedPlayers) ensurePlayerOption(select, picked);
    const decision = linkDecision(
      {
        hasPicker: !!select,
        merged: !!flagsOf(mod)._mergedPlayers,
        offersPlayer: !!select && hasPlayerOption(select, playerId),
        currentValue: select ? playerIdFromSelect(select.value) : null,
      },
      playerId,
    );
    if (decision === "none" || decision === "same" || !select) return;
    if (decision === "skip") {
      flashLinkSkipped(mod);
      return;
    }
    select.value = value;
    // The source already took the one checkpoint for the whole batch, and this module
    // mustn't spread the change back out to the group again.
    flagsOf(mod)._skipNextSaveState = true;
    flagsOf(mod)._applyingLinkedPlayer = true;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    flagsOf(mod)._applyingLinkedPlayer = false;
  });
}

/**
 * "Changes 5" in each selected module's search box while 2+ player modules are selected,
 * so it's clear a pick goes to all of them. Watches class changes rather than the store's
 * selection (05d's fork 3A): a comparison doesn't count, and whether a module is merged is
 * its `_mergedPlayers` on the element, not store state — a merge changes classes too, so
 * this sees it. Every selection path ends up toggling `.module-selected`.
 */
export function installLinkHints(workspace: HTMLElement): void {
  let queued = false;
  function refreshLinkHints(): void {
    queued = false;
    const selected = Array.from(document.querySelectorAll<HTMLElement>(".placed-module.module-selected"));
    const linkable = selected.filter((m) => m.querySelector(".player-search-wrap") && !flagsOf(m)._mergedPlayers);
    // Live: selected league modules follow a pick too (league-follow.ts), so they count —
    // but only alongside a player module, which is where the pick is made.
    const following = LIVE && linkable.length > 0 ? selected.filter(followsLeague).length : 0;
    document.querySelectorAll(".player-search-wrap[data-linked]").forEach((w) => w.removeAttribute("data-linked"));
    const hint = linkHintText(linkable.length + following);
    if (hint === null) return;
    linkable.forEach((m) => m.querySelector(".player-search-wrap")?.setAttribute("data-linked", hint));
  }
  new MutationObserver(() => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(refreshLinkHints);
    }
  }).observe(workspace, { subtree: true, childList: true, attributes: true, attributeFilter: ["class"] });
}

// ── The change handler ───────────────────────────────────────────────────────

export interface PlayerActions {
  /** The `.player-select`'s 'change' — whoever set the select's value. */
  onPlayerSelectChange: (mod: HTMLElement, select: HTMLSelectElement) => void;
}

export function createPlayerActions(
  store: ProtoStore,
  saveState: () => void,
  refreshModuleBody: (mod: HTMLElement) => void,
  idOf: (mod: HTMLElement) => string | undefined,
): PlayerActions {
  function onPlayerSelectChange(mod: HTMLElement, select: HTMLSelectElement): void {
    const flags = flagsOf(mod);
    // Only a real pick in the search box spreads to the other selected modules (it
    // records `previous`) — not the code-driven changes (drag-switch, a comparison
    // dropping back to one player, undo), which stay local to this module.
    const previous = select.dataset.previous;
    delete select.dataset.previous;
    const linked = previous === undefined || flags._applyingLinkedPlayer ? [] : linkedPlayerTargets(mod);
    // The select already holds the NEW player when this fires, and undo reads the player
    // off the select — so put the previous one back for the checkpoint. The whole linked
    // batch is this ONE step, holding everyone's old player.
    if (previous !== undefined && !flags._skipNextSaveState) {
      const next = select.value;
      select.value = previous;
      saveState();
      select.value = next;
      flags._skipNextSaveState = true;
    }
    // A module that showed a team (a Bio) shows a player again. After the checkpoint above, so
    // Undo brings the team back (teams and matches, 2026-10-30).
    delete (mod as unknown as { _team?: unknown })._team;
    // …and teams compared on a Stats tile (team-merge.ts) go with it.
    for (const key of ["_mergedTeams", "_mergedTeamColors"]) delete (mod as unknown as Record<string, unknown>)[key];
    // A map showing a match goes back to the player's map.
    for (const key of ["_mapMatch", "_mapFromPlayer", "_mapPlayer", "_mapHidden"]) delete (mod as unknown as Record<string, unknown>)[key];
    const playerId = playerIdFromSelect(select.value);
    setStickyPlayer(playerId);
    const id = idOf(mod);
    if (id === undefined) throw new Error("onPlayerSelectChange: not a live module");
    // Synchronous, so the search box shows the new name before anything below reads the
    // page (main.js synced it inside the same event).
    flushSync(() => store.getState().updateModule(id, { player: playerId }));
    // A comparison dropping back to one player already took its checkpoint before
    // removing the player — a second one here would make Undo need two presses.
    if (flags._skipNextSaveState) {
      delete flags._skipNextSaveState;
    } else {
      // Code-driven change without its own checkpoint (e.g. the "Try Katie McCabe" link):
      // taken now, so it already holds the new player — main.js's behaviour, kept.
      saveState();
    }
    // In place — no fade for a player change.
    flags._skipBodyOpacity = true;
    refreshModuleBody(mod);
    flags._skipBodyOpacity = false;
    if (linked.length && playerId !== null) {
      applyLinkedPlayer(linked, playerId);
      // Live: the selected League Table / Match Results / Squad follow to the player's league.
      if (LIVE) followPlayerLeague(linked, refreshModuleBody);
    }
  }

  return { onPlayerSelectChange };
}
